/** Retired planned routes in a rail; restoring is a restart with a new start date and schedule, keeping the stops. */
import React, { useMemo, useState } from "react"

import { format, isBefore, parseISO, startOfDay } from "date-fns"
import { Archive, CalendarIcon, Loader2, RotateCcw, Search, Undo2, X } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useRestorePlannedRoute, useRoutesPlanned } from "@/hooks/useCollections"
import { formatDayMonth, formatDayMonthYear } from "@/lib/dateFormats"
import { retiredPlannedRoutes, retirementReason, todayDateOnly } from "@/lib/plannedRouteLifecycle"
import { cn } from "@/lib/utils"
import { DayOfWeek, RoutePlanned } from "@/types/collections"

import DayToggleGroup, { DayStrip } from "./DayToggleGroup"

interface RetiredRoutesButtonProps {
  className?: string
}

interface RestoreForm {
  route: RoutePlanned
  effectiveFrom: Date
  days: DayOfWeek[]
}

const RetiredRoutesButton: React.FC<RetiredRoutesButtonProps> = ({ className }) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [form, setForm] = useState<RestoreForm | null>(null)
  const [restoringRouteId, setRestoringRouteId] = useState<string | null>(null)

  const { data: routesPlannedData } = useRoutesPlanned()
  const restorePlannedRoute = useRestorePlannedRoute()

  const today = todayDateOnly()
  const retired = useMemo(
    () => retiredPlannedRoutes((routesPlannedData?.data as RoutePlanned[]) ?? [], today),
    [routesPlannedData, today],
  )

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return retired
    return retired.filter(
      (route) =>
        route.code.toLowerCase().includes(term) ||
        (route.name ?? "").toLowerCase().includes(term) ||
        (route.depot ?? "").toLowerCase().includes(term),
    )
  }, [retired, search])

  /** Grouped by retirement; order is inherited from retiredPlannedRoutes' sort. */
  const groups = useMemo(() => {
    const byReason = new globalThis.Map<string, { label: string; routes: RoutePlanned[] }>()
    for (const route of visible) {
      const reason = retirementReason(route, today)
      const key = reason?.kind === "cancelled" ? "cancelled" : (reason?.endedOn ?? "unknown")
      const label =
        reason?.kind === "cancelled"
          ? "Cancelled"
          : reason?.endedOn
            ? `Ended ${formatDayMonthYear(parseISO(reason.endedOn))}`
            : "Retired"
      const existing = byReason.get(key)
      if (existing) existing.routes.push(route)
      else byReason.set(key, { label, routes: [route] })
    }
    return [...byReason.values()]
  }, [visible, today])

  const openRestoreForm = (route: RoutePlanned) =>
    setForm({
      route,
      // Today, not the end date: backdating would generate instances for days already gone.
      effectiveFrom: startOfDay(new Date()),
      days: route.days ?? [],
    })

  const handleRestore = async () => {
    if (!form || form.days.length === 0) return
    setRestoringRouteId(form.route.id)
    try {
      const result = await restorePlannedRoute.mutateAsync({
        routeId: form.route.id,
        effectiveFrom: format(form.effectiveFrom, "yyyy-MM-dd"),
        days: form.days,
      })
      toast.success(`${form.route.code} restored`, {
        description:
          result.instancesCreated > 0
            ? `${result.instancesCreated} daily route${
                result.instancesCreated === 1 ? "" : "s"
              } generated from ${formatDayMonth(parseISO(result.effectiveFrom))}.`
            : "The route is live again. Its daily routes will be generated on the next run.",
      })
      setForm(null)
    } catch (err) {
      toast.error("Could not restore route", {
        description:
          err instanceof Error ? err.message : "The route was not changed. Please try again.",
      })
    } finally {
      setRestoringRouteId(null)
    }
  }

  // Stays mounted while open, or restoring the last route would yank the rail shut.
  if (retired.length === 0 && !open) return null

  const isSaving = restoringRouteId === form?.route.id
  const scheduleEmpty = form !== null && form.days.length === 0

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        aria-pressed={open}
        onClick={() => setOpen(true)}
        className={cn(
          "h-8 border-palette-jungleGreen200 bg-white text-palette-jungleGreen700",
          open && "border-amber-200 bg-amber-50",
          className,
        )}
      >
        <Archive size={14} className="mr-2" />
        Retired ({retired.length})
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex flex-col">
          <SheetHeader>
            <SheetTitle>Retired routes</SheetTitle>
            <SheetDescription>
              Cancelled, or given an end date that has passed. Their stops were never deleted, so
              restoring puts them back as they were.
            </SheetDescription>
          </SheetHeader>

          <div className="relative flex-shrink-0">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-palette-jungleGreen400"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code, name or depot"
              aria-label="Search retired routes"
              className="h-8 border-palette-jungleGreen200 bg-white pl-8 pr-8 text-xs"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-palette-jungleGreen400 hover:text-palette-jungleGreen700"
              >
                <X size={13} />
              </button>
            )}
          </div>

          <p className="mt-2 flex-shrink-0 text-xs text-palette-jungleGreen500">
            {search ? (
              <>
                <span className="font-medium text-palette-jungleGreen900">{visible.length}</span> of{" "}
                {retired.length} retired routes
              </>
            ) : (
              <>
                {retired.length} retired route{retired.length === 1 ? "" : "s"}
              </>
            )}
          </p>

          <div className="-mx-5 mt-2 min-h-0 flex-1 overflow-y-auto">
            {visible.length === 0 ? (
              <p className="px-5 py-10 text-center text-xs text-palette-jungleGreen500">
                {retired.length === 0
                  ? "Nothing retired. Everything that has run is still running."
                  : `No retired route matches “${search}”.`}
              </p>
            ) : (
              <div>
                {groups.map((group) => (
                  <section key={group.label}>
                    <h4 className="sticky top-0 z-10 flex items-center justify-between border-y border-palette-jungleGreen200 bg-palette-jungleGreen000/95 px-5 py-1.5 text-[11px] font-medium text-palette-jungleGreen600 backdrop-blur">
                      <span>{group.label}</span>
                      <span className="text-palette-jungleGreen400">{group.routes.length}</span>
                    </h4>
                    <div className="divide-y divide-palette-jungleGreen200">
                      {group.routes.map((route) => {
                        const stopCount = route.stops?.length ?? 0
                        // depot is NOT NULL but often empty.
                        const depot = route.depot?.trim()
                        return (
                          <div key={route.id} className="flex items-center gap-3 px-5 py-2.5">
                            <div className="min-w-0 flex-1">
                              <p
                                className="truncate text-sm font-medium text-palette-jungleGreen900"
                                title={route.code}
                              >
                                {route.code}
                              </p>
                              <div className="mt-0.5 flex items-center gap-2 text-xs text-palette-jungleGreen500">
                                <DayStrip days={route.days ?? []} />
                                <span className="text-palette-jungleGreen300">·</span>
                                <span className="shrink-0">
                                  {stopCount} {stopCount === 1 ? "stop" : "stops"}
                                </span>
                                {depot && (
                                  <>
                                    <span className="text-palette-jungleGreen300">·</span>
                                    <span className="truncate">{depot}</span>
                                  </>
                                )}
                              </div>
                            </div>

                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 shrink-0 gap-1.5 text-xs"
                              disabled={restoringRouteId === route.id}
                              onClick={() => openRestoreForm(route)}
                            >
                              {restoringRouteId === route.id ? (
                                <>
                                  <Loader2 size={13} className="animate-spin" />
                                  Restoring…
                                </>
                              ) : (
                                <>
                                  <Undo2 size={13} />
                                  Restore
                                </>
                              )}
                            </Button>
                          </div>
                        )
                      })}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={form !== null} onOpenChange={(next) => !next && !isSaving && setForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restore {form?.route.code}</DialogTitle>
            <DialogDescription>
              Choose when it starts again and which days it runs. Its {form?.route.stops?.length ?? 0}{" "}
              planned stops are reused as they were.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <div className="grid gap-2">
              <Label className="text-xs text-palette-jungleGreen600">Effective from *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 w-full justify-start border-palette-jungleGreen200 bg-white text-sm"
                  >
                    <CalendarIcon size={14} className="mr-2 text-palette-jungleGreen500" />
                    {form ? formatDayMonthYear(form.effectiveFrom) : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent variant="plain" align="start">
                  <Calendar
                    mode="single"
                    selected={form?.effectiveFrom}
                    onSelect={(date) =>
                      date && setForm((prev) => (prev ? { ...prev, effectiveFrom: date } : prev))
                    }
                    disabled={(date) => isBefore(date, startOfDay(new Date()))}
                    className="bg-white"
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-palette-jungleGreen500">
                Daily routes are generated from this date for the next 14 days. Anything cancelled
                before it stays cancelled.
              </p>
            </div>

            <div className="grid gap-2">
              <Label className="text-xs text-palette-jungleGreen600">Runs on *</Label>
              <DayToggleGroup
                value={form?.days ?? []}
                onChange={(days) => setForm((prev) => (prev ? { ...prev, days } : prev))}
                disabled={isSaving}
              />
              {scheduleEmpty ? (
                <p className="text-xs text-red-600">
                  Pick at least one day — a route with no days would look live and generate nothing.
                </p>
              ) : (
                <p className="text-xs text-palette-jungleGreen500">
                  Starts as the schedule it had. Change it if the arrangement has moved on.
                </p>
              )}
            </div>

            <div className="rounded-md border border-palette-jungleGreen200 bg-palette-jungleGreen000/50 p-3">
              <p className="text-xs text-palette-jungleGreen700">
                Days already edited by hand, pushed to Samsara, or already run are left exactly as
                they are.
              </p>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" disabled={isSaving} onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button variant="confirm" disabled={isSaving || scheduleEmpty} onClick={handleRestore}>
              {isSaving ? (
                <>
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                  Restoring…
                </>
              ) : (
                <>
                  <RotateCcw size={14} className="mr-1.5" />
                  Restore route
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default RetiredRoutesButton
